import { useEffect, useRef, useState } from 'react';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface ResponsiveSelectProps {
  options: SelectOption[];
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Width / border / colour classes for the field — pass the same classes the old <select> had so it matches sibling inputs. */
  className?: string;
  /** Accepted for API compatibility; validation is handled by the page's own submit checks. */
  required?: boolean;
  /** Show the empty "placeholder" choice (default true). Set false for selects that always hold a value. */
  includeEmpty?: boolean;
  ariaLabel?: string;
}

/**
 * The app's standard dropdown. Renders a themed field with the option list
 * expanding directly beneath it (matching SearchableSelect's look) on every
 * screen size — unlike a native <select>, whose open list the OS draws.
 */
export default function ResponsiveSelect({
  options,
  value,
  onChange,
  placeholder = 'Select',
  disabled = false,
  className = '',
  required: _required = false,
  includeEmpty = true,
  ariaLabel,
}: ResponsiveSelectProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => String(o.value) === String(value ?? ''));

  // Close when clicking/tapping outside
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  const pick = (v: string, isDisabled?: boolean) => {
    if (isDisabled) return;
    onChange(v);
    setOpen(false);
  };

  return (
    <div
      ref={wrapRef}
      className={`relative bg-white ${className} ${
        open ? 'ring-2 ring-brand-500 border-transparent' : ''
      } ${disabled ? 'opacity-50 pointer-events-none bg-gray-100' : 'cursor-pointer'}`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-label={ariaLabel}
      onClick={() => !disabled && setOpen((v) => !v)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); }
        if (e.key === 'Escape') setOpen(false);
      }}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className={`truncate ${selected ? 'text-gray-900' : 'text-gray-400'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1.5 z-[60] bg-white border border-ink-200 rounded-xl shadow-panel overflow-hidden animate-fade-in min-w-[10rem]"
          role="listbox"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="max-h-[45vh] lg:max-h-60 overflow-y-auto py-1 overscroll-contain">
            {includeEmpty && (
              <button
                type="button"
                onClick={() => pick('')}
                className={`w-full text-left px-3.5 py-2.5 min-h-[44px] lg:min-h-0 lg:py-2 flex items-center justify-between text-sm ${
                  !selected ? 'bg-brand-50 text-brand-700 font-semibold' : 'text-gray-500 hover:bg-gray-50 active:bg-gray-50'
                }`}
              >
                {placeholder}
              </button>
            )}
            {options.length === 0 ? (
              <div className="px-3.5 py-6 text-center text-sm text-gray-400">No options available</div>
            ) : (
              options.map((o) => {
                const isSel = String(o.value) === String(value ?? '');
                return (
                  <button
                    key={o.value}
                    type="button"
                    disabled={o.disabled}
                    onClick={() => pick(String(o.value), o.disabled)}
                    className={`w-full text-left px-3.5 py-2.5 min-h-[44px] lg:min-h-0 lg:py-2 flex items-center justify-between text-sm transition-colors ${
                      o.disabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : isSel
                        ? 'bg-brand-50 text-brand-700 font-semibold'
                        : 'text-gray-800 hover:bg-gray-50 active:bg-gray-50'
                    }`}
                  >
                    <span className="truncate">{o.label}</span>
                    {isSel && (
                      <svg className="w-4 h-4 text-brand-600 shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
